/**
 * Frequently Bought Together — <fbt-component>
 *
 * State per recommended product (this.items):
 *   { productId, variantId, quantity: 1, selected, manuallyChanged, available, data, el }
 *
 * - Cards are fetched once, lazily, from Shopify's native recommendations route
 *   rendered through this same section (see sections/frequently-bought-together.liquid).
 * - The main product's variant is followed via the theme's `variant:update` event.
 *   Only items with manuallyChanged === false are re-synced.
 * - The CTA adds the MAIN product (current variant, qty 1) + every selected pick in ONE
 *   /cart/add.js request; the subtotal includes the main product. Products already in
 *   the cart are never recommended.
 * - "Add all to cart" posts every selected item in ONE /cart/add.js request, then
 *   dispatches the theme's CartAddEvent so the cart drawer, cart bubble and buy box
 *   refresh through their existing listeners.
 */
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Hidden line-item property used for attribution. Keys starting with "_" are hidden
 * by the theme's cart (snippets/cart-products.liquid) and by Shopify checkout, but are
 * stored on the order line, so FBT revenue can be identified in orders and exports.
 */
const ATTRIBUTION = Object.freeze({ _source: 'frequently_bought_together' });

const FETCH_LIMIT = 10; // Shopify's per-request maximum for the recommendations API.
const MAX_POOL = 25; // Hard ceiling for the carousel.
const DEFAULT_SELECTED = 6; // Size of the default (pre-checked) bundle.

/** Normalise option values so `8"`, `8 inch`, `8 Inches` and `8in` all match. */
function normalizeValue(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/(inches|inch|["”″]|''|in)$/, 'in')
    .replace(/(centimetres|centimeters|cms|cm)$/, 'cm')
    .replace(/\.0+(?=[a-z]*$)/, '');
}

function optionName(option) {
  return normalizeValue(typeof option === 'string' ? option : option?.name);
}

function variantOptions(variant) {
  if (!variant) return [];
  if (Array.isArray(variant.options)) return variant.options;
  return [variant.option1, variant.option2, variant.option3].filter((v) => v != null);
}

class FbtComponent extends HTMLElement {
  /** @type {Array<any>} */
  items = [];
  cartVariantIds = new Set();
  #abort = new AbortController();
  #loaded = false;
  #busy = false;
  #openPanel = null;

  connectedCallback() {
    this.track = this.querySelector('[data-fbt-track]');
    this.subtotalEl = this.querySelector('[data-fbt-subtotal]');
    this.countEl = this.querySelector('[data-fbt-count]');
    this.addButton = this.querySelector('[data-fbt-add]');
    this.addLabel = this.querySelector('[data-fbt-add-label]');
    this.statusEl = this.querySelector('[data-fbt-status]');
    this.includesEl = this.querySelector('[data-fbt-includes]');
    this.arrows = this.querySelector('[data-fbt-arrows]');
    this.prevButton = this.querySelector('[data-fbt-prev]');
    this.nextButton = this.querySelector('[data-fbt-next]');

    try {
      const main = JSON.parse(this.querySelector('[data-fbt-main]')?.textContent || '{}');
      this.mainProductId = String(main.productId ?? this.dataset.productId);
      this.mainOptionNames = (main.optionNames || []).map(optionName);
      this.mainVariant = main.variant || null;
      this.mainTitle = main.title || '';
    } catch {
      this.mainProductId = String(this.dataset.productId);
      this.mainOptionNames = [];
      this.mainVariant = null;
    }

    const { signal } = this.#abort;
    document.addEventListener(ThemeEvents.variantUpdate, this.#onMainVariantUpdate, { signal });
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.addButton?.addEventListener('click', () => this.addAllToCart(), { signal });
    this.prevButton?.addEventListener('click', () => this.#scrollByPage(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.#scrollByPage(1), { signal });
    this.track?.addEventListener('scroll', this.#updateArrows, { signal, passive: true });
    window.addEventListener('resize', this.#updateArrows, { signal, passive: true });
    window.addEventListener('resize', this.#refitLabels, { signal, passive: true });

    // Curated set (Diwali): the first cards are already in the page HTML, so show them right
    // away; the rest of the carousel is fetched in the background. Other products: unchanged.
    if (this.dataset.curated === 'true') this.#loadCurated();
    else this.#observeAndLoad();
  }

  disconnectedCallback() {
    clearTimeout(this.startTimer);
    this.#abort.abort();
    this.observer?.disconnect();
  }

  /* ------------------------------------------------------------------ loading */

  /**
   * Performance: FBT only starts fetching after the page has finished loading (or 3s at most),
   * in an idle moment, and when the shopper is near the section. This keeps its requests from
   * competing with the product page itself and the reviews widget below it.
   */
  #observeAndLoad() {
    const observe = () => {
      if (!('IntersectionObserver' in window)) return this.load();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.observer.disconnect();
            this.load();
          }
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      // The component itself may be `hidden`, so observe its section wrapper.
      this.observer.observe(this.parentElement || this);
    };

    if (window.Shopify?.designMode) return observe(); // theme editor: no waiting

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      clearTimeout(this.startTimer);
      if ('requestIdleCallback' in window) window.requestIdleCallback(observe, { timeout: 1500 });
      else setTimeout(observe, 50);
    };
    if (document.readyState === 'complete') start();
    else {
      window.addEventListener('load', start, { once: true, signal: this.#abort.signal });
      this.startTimer = setTimeout(start, 3000); // never wait forever on a slow third-party script
    }
  }

  async load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const max = Math.min(MAX_POOL, Number(this.dataset.max) || MAX_POOL);
    const mode = this.dataset.intent || 'complementary_then_related';

    // Priority order: Complementary -> Recommended -> Related (collection fallback).
    // The two recommendation calls run in parallel; the fallback only runs if the pool is short.
    const primary = [];
    if (mode !== 'related') primary.push(['complementary', this.#recommendationsUrl('complementary')]);
    if (mode !== 'complementary') primary.push(['recommended', this.#recommendationsUrl('related')]);

    const pool = [];
    const seen = new Set([this.mainProductId]); // Global de-dupe by Shopify product ID; main product excluded.
    const take = (source, cards) => {
      for (const card of cards) {
        if (pool.length >= max) return;
        const id = card.dataset.productId;
        if (!id || seen.has(id) || !this.#isPurchasable(card)) continue;
        seen.add(id);
        card.dataset.fbtSource = source;
        pool.push(card);
      }
    };

    const curated = false; // Curated sets load through #loadCurated() instead.

    {
      // Products already in the cart are never recommended (one /cart.js read, in parallel).
      const [inCart, batches] = await Promise.all([
        this.#cartProductIds(),
        Promise.all(primary.map(([source, url]) => this.#fetchCards(url).then((cards) => [source, cards]))),
      ]);
      inCart.forEach((id) => seen.add(id));
      batches.forEach(([source, cards]) => take(source, cards));
    }

    if (!curated && mode === 'complementary_then_related') {
      const handles = (this.dataset.fallbackCollections || '').split(',').filter(Boolean);
      for (const handle of handles) {
        if (pool.length >= max) break;
        take('related', await this.#fetchCards(this.#collectionUrl(handle)));
      }
    }

    if (!pool.length) {
      this.#hideSection();
      return;
    }

    this.track.replaceChildren(...pool);
    this.querySelector('[data-fbt-editor-note]')?.remove();
    this.items = pool.map((el) => this.#hydrateCard(el)).filter(Boolean);
    pool.forEach((el) => {
      if (!this.items.some((item) => item.el === el)) el.remove();
    });

    if (!this.items.length) {
      this.#hideSection();
      return;
    }

    // Default bundle: the first N (highest priority) products are pre-checked; the rest start unchecked.
    // (Curated set too: the 2 pinned products + the next 4 are checked.)
    const preselect = Math.max(0, Number(this.dataset.defaultSelected) || DEFAULT_SELECTED);
    this.items.forEach((item, index) => {
      item.source = item.el.dataset.fbtSource || '';
      item.selected = index < preselect;
    });

    // Curated set: a preferred size (e.g. "15 Inch") for one collection's products. It is locked so
    // the main product's size changes don't overwrite it; the shopper can still change it.
    if (curated) this.#applyPreferredSize();

    // Default variant logic: inherit the main product's variant where possible.
    this.items.forEach((item) => this.#applyAutoVariant(item));
    this.#placeCards();

    this.hidden = false;
    this.render();
    requestAnimationFrame(this.#updateArrows);

    this.#track('fbt_impression', {
      productId: this.mainProductId,
      recommendations: this.items.map((i) => ({
        productId: i.productId,
        variantId: i.variantId,
        source: i.source,
        selected: i.selected,
      })),
    });
  }

  /* ----------------------------------------------------------- curated set */

  /**
   * Curated set (e.g. Diwali), loaded in two steps so it never feels slow:
   *   1. Instantly: the pinned + slot products already rendered into the page HTML (no network).
   *      Products in the cart come from the page too (data-cart-product-ids).
   *   2. In the background, as the shopper scrolls near the section: the curated collections
   *      (small 12-product pages) fill the remaining cards and any slot whose preferred product
   *      can't be shown. Cards already on screen keep their state (ticks, sizes).
   */
  #loadCurated() {
    if (this.#loaded) return;
    this.#loaded = true;
    const max = Math.min(MAX_POOL, Number(this.dataset.max) || MAX_POOL);
    const pageCart = (this.dataset.cartProductIds || '').split(',').filter(Boolean);

    this.#applyCuratedPool(this.#curatedPool(max, pageCart, new Map()), false);

    this.#whenNear(async () => {
      const handles = (this.dataset.curatedCollections || '').split(',').filter(Boolean);
      const slotHandles = this.#slots().map((s) => s.collection).filter(Boolean);
      const allHandles = [...new Set([...handles, ...slotHandles])];
      const [cart, lists] = await Promise.all([
        this.#readCart(),
        Promise.all(allHandles.map((handle) => this.#fetchCards(this.#collectionUrl(handle, 'fbt-curated')))),
      ]);
      if (!this.isConnected) return;
      const byHandle = new Map(allHandles.map((handle, i) => [handle, lists[i]]));
      // Cards already showing stay put, even if the live cart now has them (e.g. just added).
      const shown = new Set(this.items.map((item) => item.productId));
      const inCart = cart ? (cart.items || []).map((line) => String(line.product_id)) : pageCart;
      this.#applyCuratedPool(
        this.#curatedPool(max, inCart.filter((id) => !shown.has(id)), byHandle),
        true
      );
    });
  }

  /** Pinned first, then slots (preferred product or a similar one), then the collections round-robin. */
  #curatedPool(max, inCartIds, byHandle) {
    const pool = [];
    const seen = new Set([this.mainProductId, ...inCartIds]);
    const take = (source, cards) => {
      for (const card of cards) {
        if (pool.length >= max) return;
        const id = card.dataset.productId;
        if (!id || seen.has(id) || !this.#isPurchasable(card)) continue;
        seen.add(id);
        card.dataset.fbtSource = source;
        pool.push(card);
      }
    };

    take('pinned', this.#pinnedCards());

    for (const slot of this.#slots()) {
      if (pool.length >= max) break;
      const before = pool.length;
      take('slot', slot.cards);
      if (pool.length > before || !slot.collection) continue;
      for (const card of byHandle.get(slot.collection) || []) {
        if (!this.#matchesSlot(card, slot.match)) continue;
        take(`collection:${slot.collection}`, [card.cloneNode(true)]);
        if (pool.length > before) break;
      }
    }

    const handles = (this.dataset.curatedCollections || '').split(',').filter(Boolean);
    const queues = handles.map((handle) => ({
      source: `collection:${handle}`,
      cards: (byHandle.get(handle) || []).map((card) => card.cloneNode(true)),
    }));
    while (pool.length < max && queues.some((q) => q.cards.length)) {
      for (const queue of queues) {
        if (pool.length >= max) break;
        while (queue.cards.length) {
          const before = pool.length;
          take(queue.source, [queue.cards.shift()]);
          if (pool.length > before) break;
        }
      }
    }
    return pool;
  }

  /** Shows `pool`, re-using cards already on screen so their ticks / sizes are kept. */
  #applyCuratedPool(pool, final) {
    const existing = new Map(this.items.map((item) => [item.productId, item]));
    const preselect = Math.max(0, Number(this.dataset.defaultSelected) || DEFAULT_SELECTED);
    const items = [];

    for (const card of pool) {
      const old = existing.get(card.dataset.productId);
      if (old) {
        existing.delete(old.productId);
        items.push(old);
        continue;
      }
      const item = this.#hydrateCard(card);
      if (!item) continue;
      item.source = card.dataset.fbtSource || '';
      // First N positions start ticked — unless the shopper has already changed ticks.
      item.selected = !this.userTouched && items.length < preselect;
      item.fresh = true;
      items.push(item);
    }
    existing.forEach((item) => item.el.remove());

    if (!items.length) {
      this.items = [];
      if (final) this.#hideSection();
      return;
    }

    this.items = items;
    this.track.replaceChildren(...items.map((item) => item.el));
    this.querySelector('[data-fbt-editor-note]')?.remove();

    this.#applyPreferredSize();
    items.forEach((item) => {
      if (item.fresh) this.#applyAutoVariant(item);
      item.fresh = false;
    });
    this.#placeCards();

    this.hidden = false;
    this.render();
    requestAnimationFrame(this.#updateArrows);

    if (final) {
      this.#track('fbt_impression', {
        productId: this.mainProductId,
        recommendations: this.items.map((i) => ({
          productId: i.productId,
          variantId: i.variantId,
          source: i.source,
          selected: i.selected,
        })),
      });
    }
  }

  /** Runs `fn` once the section is within ~1.5 screens of the viewport (no wait for page load). */
  #whenNear(fn) {
    if (!('IntersectionObserver' in window)) return fn();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        fn();
      },
      { rootMargin: '0px 0px 1200px 0px' }
    );
    this.observer.observe(this.parentElement || this);
  }

  /** Pinned products are rendered server-side into a <template> on the product page. */
  #pinnedCards() {
    const template = this.querySelector('template[data-fbt-pinned]');
    if (!template) return [];
    return Array.from(template.content.querySelectorAll('[data-fbt-card]')).map((n) => document.importNode(n, true));
  }

  /** Slot templates rendered server-side by the section (preferred product + fallback collection). */
  #slots() {
    return Array.from(this.querySelectorAll('template[data-fbt-slot]')).map((template) => ({
      cards: Array.from(template.content.querySelectorAll('[data-fbt-card]')).map((n) => document.importNode(n, true)),
      collection: template.dataset.collection || '',
      match: (template.dataset.match || '')
        .split(',')
        .map((w) => w.trim().toLowerCase())
        .filter(Boolean),
    }));
  }

  #matchesSlot(card, words) {
    if (!words.length) return true;
    let title = '';
    try {
      title = JSON.parse(card.querySelector('[data-fbt-data]')?.textContent || '{}').title || '';
    } catch {
      return false;
    }
    title = title.toLowerCase();
    return words.some((w) => title.includes(w));
  }

  #applyPreferredSize() {
    const handle = this.dataset.prefSizeCollection;
    const wanted = normalizeValue(this.dataset.prefSizeValue);
    if (!handle || !wanted) return;
    const pinnedIds = (this.dataset.prefSizeProducts || '').split(',').filter(Boolean);
    for (const item of this.items) {
      if (item.fresh === false) continue; // already on screen: keep the shopper's / earlier size
      if (item.source !== `collection:${handle}` && !pinnedIds.includes(item.productId)) continue;
      const match = item.data.variants.find((v) => v.available && variantOptions(v).some((o) => normalizeValue(o) === wanted));
      if (!match) continue;
      item.variantId = match.id;
      item.presetLocked = true;
    }
  }

  /**
   * Mobile two-row layout: cards are placed page by page (6 per page = 2 rows x 3) so the first
   * products fill the top row first: page 1 = [1 2 3] over [4 5 6], page 2 = [7 8 9] over [10 11 12]…
   * Desktop ignores these variables (single row).
   */
  #placeCards() {
    this.items.forEach((item, index) => {
      const page = Math.floor(index / 6);
      const k = index % 6;
      item.el.style.setProperty('--fbt-row', String(Math.floor(k / 3) + 1));
      item.el.style.setProperty('--fbt-col', String(page * 3 + (k % 3) + 1));
    });
  }

  async #cartProductIds() {
    const cart = await this.#readCart();
    return cart ? (cart.items || []).map((line) => String(line.product_id)) : [];
  }

  /** Reads /cart.js and remembers which variants are in it (used to avoid re-adding the main product). */
  async #readCart() {
    try {
      const response = await fetch(`${this.dataset.cartUrl || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#abort.signal,
      });
      if (!response.ok) return null;
      const cart = await response.json();
      this.#rememberCart(cart);
      return cart;
    } catch {
      return null; // Never block recommendations on a cart read.
    }
  }

  #rememberCart(cart) {
    this.cartVariantIds = new Set((cart?.items || []).map((line) => String(line.variant_id)));
  }

  /** Keep "is the main product already in the cart?" current after any cart change on the page. */
  #onCartUpdate = (event) => {
    const resource = event.detail?.resource;
    if (Array.isArray(resource?.items)) {
      this.#rememberCart(resource);
      if (this.items.length) this.render();
      return;
    }
    this.cartStale = true;
    clearTimeout(this.cartTimer);
    this.cartTimer = setTimeout(() => this.#refreshCart(), 150);
  };

  async #refreshCart() {
    clearTimeout(this.cartTimer);
    this.cartStale = false;
    await this.#readCart();
    if (this.items.length && !this.#busy) this.render();
  }

  #recommendationsUrl(intent) {
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('product_id', this.mainProductId);
    url.searchParams.set('limit', String(FETCH_LIMIT));
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('intent', intent);
    return url.toString();
  }

  #collectionUrl(handle, view = 'fbt-fallback') {
    const base = (this.dataset.collectionsUrl || '/collections').replace(/\/$/, '');
    return `${base}/${encodeURIComponent(handle)}?view=${view}`;
  }

  /** A card is eligible only if its data parses and at least one variant is available. */
  #isPurchasable(card) {
    try {
      const data = JSON.parse(card.querySelector('[data-fbt-data]')?.textContent || 'null');
      return Boolean(data && Array.isArray(data.variants) && data.variants.some((v) => v.available));
    } catch {
      return false;
    }
  }

  async #fetchCards(url) {
    try {
      const response = await fetch(url, { signal: this.#abort.signal });
      if (!response.ok) return [];
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = html.querySelector('[data-fbt-results]');
      return results ? Array.from(results.querySelectorAll('[data-fbt-card]')).map((n) => document.importNode(n, true)) : [];
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[FBT] recommendations failed', error);
      return [];
    }
  }

  #hideSection() {
    if (window.Shopify?.designMode) {
      this.hidden = false;
      return;
    }
    this.hidden = true;
    const section = this.closest('.shopify-section');
    if (section) section.hidden = true;
  }

  #hydrateCard(el) {
    let data;
    try {
      data = JSON.parse(el.querySelector('[data-fbt-data]')?.textContent || 'null');
    } catch {
      data = null;
    }
    if (!data || !Array.isArray(data.variants)) return null;

    const firstAvailable = data.variants.find((v) => v.available);
    if (!firstAvailable) return null; // No purchasable variant: exclude.

    const img = el.querySelector('.fbt-card__img');
    const item = {
      productId: String(data.productId),
      variantId: data.defaultVariantId,
      quantity: 1,
      selected: true,
      manuallyChanged: false,
      available: true,
      data,
      el,
      checkbox: el.querySelector('[data-fbt-checkbox]'),
      toggle: el.querySelector('[data-fbt-variant-toggle]'),
      panel: el.querySelector('[data-fbt-variant-panel]'),
      label: el.querySelector('[data-fbt-variant-label]'),
      priceEl: el.querySelector('[data-fbt-price]'),
      compareEl: el.querySelector('[data-fbt-compare]'),
      img,
      baseImage: img ? { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') } : null,
    };

    if (!data.variants.some((v) => v.id === item.variantId && v.available)) item.variantId = firstAvailable.id;
    item.panelHome = item.panel?.parentElement || null;

    item.checkbox?.addEventListener('change', () => this.#onToggleSelected(item));
    item.toggle?.addEventListener('click', () => (item.panel.hidden ? this.#openVariantPanel(item) : this.#closeVariantPanel(item)));
    item.panel?.querySelector('[data-fbt-variant-close]')?.addEventListener('click', () => this.#closeVariantPanel(item, true));
    item.panel?.addEventListener('change', (event) => {
      const radio = event.target.closest('[data-fbt-variant-radio]');
      if (radio) this.#onManualVariant(item, Number(radio.value), event);
    });
    item.panel?.addEventListener('click', (event) => {
      // Mouse / touch pick closes the sheet; keyboard arrows keep it open for browsing.
      if (event.target.closest('.fbt-pill') && event.detail > 0) {
        requestAnimationFrame(() => this.#closeVariantPanel(item, true));
      }
    });
    item.panel?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' || event.key === 'Enter') {
        event.preventDefault();
        this.#closeVariantPanel(item, true);
      }
    });

    return item;
  }

  /* ---------------------------------------------------------- variant logic */

  /** Best available variant of `item` for the main product's current variant, or null. */
  #matchMainVariant(item) {
    const mainValues = variantOptions(this.mainVariant).map(normalizeValue);
    if (!mainValues.length || mainValues.every((v) => v === 'defaulttitle')) return null;

    const mainByName = new Map();
    this.mainOptionNames.forEach((name, i) => mainByName.set(name, mainValues[i]));

    const recNames = (item.data.optionNames || []).map(optionName);
    let best = null;
    let bestScore = 0;

    for (const variant of item.data.variants) {
      if (!variant.available) continue; // Never auto-select sold out.
      let score = 0;
      variantOptions(variant).forEach((raw, i) => {
        const value = normalizeValue(raw);
        const name = recNames[i];
        if (mainByName.has(name)) {
          if (mainByName.get(name) === value) score += 2;
        } else if (mainValues.includes(value)) {
          score += 1; // Same value under a differently named option (e.g. "Size" vs "Diameter").
        }
      });
      if (score > bestScore) {
        best = variant;
        bestScore = score;
      }
    }
    return best;
  }

  #applyAutoVariant(item) {
    if (item.manuallyChanged || item.presetLocked || !item.available) return false;
    const target = this.#matchMainVariant(item) || item.data.variants.find((v) => v.available);
    if (!target || target.id === item.variantId) return false;
    item.variantId = target.id;
    return true;
  }

  #onMainVariantUpdate = (event) => {
    const detail = event.detail;
    if (!detail?.resource || String(detail.data?.productId) !== this.mainProductId) return;
    if (event.target instanceof Element && event.target.closest('product-card, .quick-add-modal, quick-add-dialog')) return;

    this.mainVariant = detail.resource;
    if (!this.items.length) return;

    this.items.forEach((item) => this.#applyAutoVariant(item));
    this.render(); // Always: the main product's price is part of the subtotal.
  };

  #onManualVariant(item, variantId, event) {
    const variant = item.data.variants.find((v) => v.id === variantId);
    if (!variant?.available) return;
    const previous = item.variantId;
    item.variantId = variantId;
    item.manuallyChanged = true;
    this.render();
    if (previous !== variantId) {
      this.#track('fbt_variant_changed', {
        productId: item.productId,
        variantId,
        previousVariantId: previous,
        source: event?.isTrusted ? 'customer' : 'script',
      });
    }
  }

  #onToggleSelected(item) {
    item.selected = item.checkbox.checked;
    item.userDeselected = !item.selected; // only a shopper's own untick de-emphasises the card
    this.userTouched = true; // cards loaded later won't be auto-ticked over the shopper's choice
    this.render();
    this.#track(item.selected ? 'fbt_product_selected' : 'fbt_product_deselected', {
      productId: item.productId,
      variantId: item.variantId,
    });
  }

  /* -------------------------------------------------------------- popover */

  /**
   * Size picker: a native <dialog> (top layer) styled like the theme's quick-add modal —
   * a bottom sheet on mobile and a centred pop-up on desktop — so it pops out over the page
   * instead of being clipped by the card image.
   * The card's own panel element is moved into the sheet while open and moved back on close,
   * so all existing radio/keyboard/analytics handlers keep working unchanged.
   */
  #ensureSheet() {
    if (this.sheet) return this.sheet;
    const sheet = document.createElement('dialog');
    sheet.className = 'fbt-sheet';
    sheet.innerHTML =
      '<div class="fbt-sheet__inner">' +
      '<div class="fbt-sheet__product">' +
      '<img class="fbt-sheet__img" alt="" width="72" height="72">' +
      '<div class="fbt-sheet__info"><p class="fbt-sheet__title"></p><p class="fbt-sheet__price"></p></div>' +
      '</div>' +
      '<div class="fbt-sheet__body"></div>' +
      '</div>';
    // Tap on the dimmed backdrop closes (the backdrop click targets the <dialog> itself).
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) this.#closeVariantPanel(this.#openPanel, true);
    });
    // Escape key / Android back gesture.
    sheet.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#closeVariantPanel(this.#openPanel, true);
    });
    this.append(sheet);
    this.sheet = sheet;
    return sheet;
  }

  #fillSheet(item) {
    if (!this.sheet) return;
    const variant = this.#variantOf(item);
    const img = this.sheet.querySelector('.fbt-sheet__img');
    const src = item.img?.currentSrc || item.img?.getAttribute('src') || '';
    if (src) {
      img.src = src;
      img.hidden = false;
    } else {
      img.hidden = true;
    }
    this.sheet.querySelector('.fbt-sheet__title').textContent = item.data.title;
    this.sheet.querySelector('.fbt-sheet__price').textContent = variant?.priceFormatted || '';
  }

  #showSheet(item) {
    const sheet = this.#ensureSheet();
    this.#fillSheet(item);
    sheet.setAttribute('aria-label', item.panel.getAttribute('aria-label') || item.data.title);
    item.panel.removeAttribute('role'); // the sheet is the dialog now
    sheet.querySelector('.fbt-sheet__body').append(item.panel);
    if (!sheet.open) {
      if (typeof sheet.showModal === 'function') sheet.showModal();
      else sheet.setAttribute('open', '');
    }
  }

  #hideSheet(item) {
    if (item.panelHome && item.panel.parentElement !== item.panelHome) {
      item.panelHome.append(item.panel);
      item.panel.setAttribute('role', 'dialog');
    }
    if (this.sheet?.open) {
      if (typeof this.sheet.close === 'function') this.sheet.close();
      else this.sheet.removeAttribute('open');
    }
  }

  #openVariantPanel(item) {
    if (this.#openPanel && this.#openPanel !== item) this.#closeVariantPanel(this.#openPanel);
    item.panel.hidden = false;
    item.toggle.setAttribute('aria-expanded', 'true');
    item.el.classList.add('is-picking');
    this.#openPanel = item;
    this.#showSheet(item);
    const checked = item.panel.querySelector('[data-fbt-variant-radio]:checked') || item.panel.querySelector('[data-fbt-variant-radio]:not(:disabled)');
    checked?.focus({ preventScroll: true });
  }

  #closeVariantPanel(item, returnFocus = false) {
    if (!item?.panel || item.panel.hidden) return;
    item.panel.hidden = true;
    this.#hideSheet(item);
    item.toggle.setAttribute('aria-expanded', 'false');
    item.el.classList.remove('is-picking');
    if (this.#openPanel === item) this.#openPanel = null;
    if (returnFocus) item.toggle.focus({ preventScroll: true });
  }

  #onDocumentPointerDown = (event) => {
    const item = this.#openPanel;
    if (!item) return;
    if (item.panel.contains(event.target) || item.toggle.contains(event.target)) return;
    if (this.sheet?.contains(event.target)) return; // sheet handles its own backdrop taps
    this.#closeVariantPanel(item);
  };

  /* --------------------------------------------------------------- render */

  /** 'included' | 'in-cart' | 'unavailable' for the main product's current variant. */
  #mainState() {
    const v = this.mainVariant;
    if (!v?.id || v.available === false) return 'unavailable';
    if (this.cartVariantIds.has(String(v.id))) return 'in-cart';
    return 'included';
  }

  /** The main product as a bundle line (current variant, qty 1), or null if it isn't added. */
  #mainLine() {
    const v = this.mainVariant;
    if (this.#mainState() !== 'included') return null;
    return {
      isMain: true,
      productId: this.mainProductId,
      variantId: v.id,
      quantity: 1,
      selected: true,
      available: true,
      data: { title: this.mainTitle, variants: [{ id: v.id, title: v.title || '', price: Number(v.price) || 0 }] },
    };
  }

  #variantOf(item) {
    return item.data.variants.find((v) => v.id === item.variantId);
  }

  render() {
    let subtotal = 0;
    let count = 0;

    for (const item of this.items) {
      const variant = this.#variantOf(item);
      if (!variant) continue;

      item.el.dataset.selected = String(item.selected && item.available);
      item.el.toggleAttribute('data-unavailable', !item.available);
      item.el.toggleAttribute('data-deselected', Boolean(item.userDeselected) && !item.selected && item.available);
      if (item.checkbox) {
        item.checkbox.checked = item.selected && item.available;
        item.checkbox.disabled = !item.available;
      }

      if (item.priceEl) item.priceEl.textContent = variant.priceFormatted;
      if (item.compareEl) {
        item.compareEl.textContent = variant.compareFormatted || '';
        item.compareEl.hidden = !variant.compareFormatted;
      }

      if (item.label) this.#fitLabel(item, variant.title);
      if (item.toggle) {
        const current = item.toggle.getAttribute('aria-label') || '';
        item.toggle.setAttribute('aria-label', current.replace(/currently .*$/, `currently ${variant.title}`));
      }
      const radio = item.panel?.querySelector(`[data-fbt-variant-radio][value="${variant.id}"]`);
      if (radio && !radio.checked) radio.checked = true;

      if (item.img) {
        const src = variant.image || item.baseImage?.src;
        const srcset = variant.image ? variant.srcset : item.baseImage?.srcset;
        if (src && item.img.getAttribute('src') !== src) {
          item.img.setAttribute('src', src);
          if (srcset) item.img.setAttribute('srcset', srcset);
          else item.img.removeAttribute('srcset');
        }
      }

      if (item.selected && item.available) {
        subtotal += Number(variant.price) || 0;
        count += 1;
      }
    }

    if (this.sheet?.open && this.#openPanel) this.#fillSheet(this.#openPanel);

    // The bundle = main product + selected picks.
    const main = this.#mainLine();
    const picks = count;
    if (main) {
      subtotal += main.data.variants[0].price;
      count += 1;
    }
    if (this.includesEl) {
      const state = this.#mainState();
      const variantTitle = this.mainVariant?.title && this.mainVariant.title !== 'Default Title' ? ` — ${this.mainVariant.title}` : '';
      const name = `${this.mainTitle}${variantTitle}`;
      this.includesEl.textContent =
        state === 'included'
          ? `Includes this item: ${name}`
          : state === 'in-cart'
            ? '' // Hidden by request: duplicate protection still applies, no message shown.
            : `${name} is sold out, so only the selected picks will be added.`;
      this.includesEl.dataset.state = state;
    }

    if (this.subtotalEl) this.subtotalEl.textContent = this.#money(subtotal);
    if (this.countEl) this.countEl.textContent = count ? `(${count} ${count === 1 ? 'item' : 'items'})` : '';
    if (this.addButton && !this.#busy) {
      this.addButton.disabled = picks === 0; // No picks = nothing beyond the regular Add to cart.
      const price = this.#money(subtotal);
      this.addLabel.textContent = picks === 0 ? 'Select an item' : `Add ${count} to cart — ${price}`;
    }
  }

  /**
   * Shows the full variant title in the size chip. Only if it still overflows the chip
   * (very narrow phones + long titles) is it abbreviated, e.g. "10 inches" -> 10". The
   * full title stays in the chip's aria-label and title tooltip.
   */
  #fitLabel(item, title) {
    const label = item.label;
    label.textContent = title;
    item.toggle?.setAttribute('title', title);
    if (!label.clientWidth || label.scrollWidth <= label.clientWidth + 1) return;
    const compact = title
      .replace(/(\d)\s*(inches|inch|in\.?)(?=\s*(\/|$))/gi, '$1"')
      .replace(/(\d)\s*(centimetres|centimeters|cms)(?=\s*(\/|$))/gi, '$1cm')
      .replace(/(\d)\s+mm\b/gi, '$1mm')
      .replace(/\s*\/\s*/g, '/');
    // Narrow cards (e.g. 3 per row on mobile): try progressively shorter forms, keeping the size.
    const parts = compact.split('/');
    const size = parts.find((part) => /\d\s*("|cm)$/.test(part)) || parts[parts.length - 1];
    const candidates = [compact, parts.filter((part) => !/mm$/i.test(part)).join('/'), size];
    for (const candidate of candidates) {
      if (!candidate || candidate === label.textContent) continue;
      label.textContent = candidate;
      if (label.scrollWidth <= label.clientWidth + 1) return;
    }
  }

  #refitLabels = () => {
    cancelAnimationFrame(this.fitFrame);
    this.fitFrame = requestAnimationFrame(() => {
      for (const item of this.items) {
        const variant = this.#variantOf(item);
        if (item.label && variant) this.#fitLabel(item, variant.title);
      }
    });
  };

  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'INR';
    try {
      return formatMoney(cents, format, currency);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /* ------------------------------------------------------------- carousel */

  #scrollByPage(direction) {
    const track = this.track;
    if (!track) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const card = track.querySelector('[data-fbt-card]');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : 0;

    if (!step) {
      track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: reduce ? 'auto' : 'smooth' });
      return;
    }

    // Whole cards per page (6 on desktop, 2 on mobile's 2.5 layout), snapped to card boundaries.
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / step + 0.01));
    const max = track.scrollWidth - track.clientWidth;
    const current = Math.round(Math.abs(track.scrollLeft) / step);
    const target = Math.min(max, Math.max(0, (current + direction * perPage) * step));
    const rtl = getComputedStyle(track).direction === 'rtl';
    track.scrollTo({ left: rtl ? -target : target, behavior: reduce ? 'auto' : 'smooth' });
  }

  #updateArrows = () => {
    const track = this.track;
    if (!track) return;
    const overflowing = track.scrollWidth - track.clientWidth > 2;
    this.classList.toggle('fbt--fits', !overflowing);
    if (this.arrows) this.arrows.hidden = !overflowing;
    if (!overflowing) return;
    const media = track.querySelector('.fbt-card__media');
    if (media?.offsetHeight) this.style.setProperty('--fbt-arrow-top', `${media.offsetTop + media.offsetHeight / 2}px`);
    const max = track.scrollWidth - track.clientWidth;
    // Handle RTL (scrollLeft is negative) as well as LTR.
    const pos = Math.abs(track.scrollLeft);
    if (this.prevButton) this.prevButton.disabled = pos <= 2;
    if (this.nextButton) this.nextButton.disabled = pos >= max - 2;
  };

  /* ---------------------------------------------------------------- cart */

  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  async #postAdd(items, sections) {
    const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, ...(sections ? { sections } : {}) }),
    });
    let data = {};
    try {
      data = await response.json();
    } catch {
      /* non-JSON error page */
    }
    const failed = !response.ok || (data && data.status && data.status >= 400);
    return { ok: !failed, data };
  }

  #setStatus(message, state) {
    if (!this.statusEl) return;
    this.statusEl.textContent = '';
    this.statusEl.removeAttribute('data-state');
    if (!message) return;
    if (state) this.statusEl.dataset.state = state;
    if (typeof message === 'string') this.statusEl.textContent = message;
    else this.statusEl.append(message);
  }

  #setBusy(busy) {
    this.#busy = busy;
    if (!this.addButton) return;
    this.addButton.disabled = busy;
    this.addButton.setAttribute('aria-busy', String(busy));
    if (busy) this.addLabel.textContent = 'Adding…';
  }

  #markUnavailable(item) {
    if (item.isMain) {
      this.mainVariant = { ...this.mainVariant, available: false };
      return;
    }
    item.available = false;
    item.selected = false;
  }

  #nameOf(item) {
    const variant = this.#variantOf(item);
    return variant && variant.title !== 'Default Title' ? `${item.data.title} (${variant.title})` : item.data.title;
  }

  async addAllToCart() {
    if (this.#busy) return;
    const picks = this.items.filter((i) => i.selected && i.available);
    if (!picks.length) return;
    // If the cart just changed (e.g. the regular Add to cart), re-read it first so the main
    // product is never added twice.
    if (this.cartStale) await this.#refreshCart();
    const main = this.#mainLine();
    const selected = main ? [main, ...picks] : picks;
    // Picks carry the hidden attribution property; the main product line stays untagged so it
    // merges with any existing cart line for the same variant.
    const lineFor = (i) => ({ id: i.variantId, quantity: i.quantity, ...(i.isMain ? {} : { properties: { ...ATTRIBUTION } }) });

    this.#closeVariantPanel(this.#openPanel);
    this.#setStatus('');
    this.#setBusy(true);

    const payloadItems = selected.map(lineFor);
    const sections = this.#cartSectionIds();

    this.#track('fbt_add_all_to_cart', {
      productId: this.mainProductId,
      items: payloadItems,
      subtotal: selected.reduce((sum, i) => sum + (Number(this.#variantOf(i)?.price) || 0), 0),
    });

    try {
      // 1) One request for the whole bundle.
      const result = await this.#postAdd(payloadItems, sections);

      if (result.ok) {
        await this.#onAdded(selected, [], result.data?.sections);
        return;
      }

      // 2) Shopify rejects the whole batch if any line fails. Retry line by line so
      //    we add everything we can and can name exactly what failed.
      const added = [];
      const failed = [];
      let lastSections = null;
      for (const item of selected) {
        const single = await this.#postAdd([lineFor(item)], sections);
        if (single.ok) {
          added.push(item);
          lastSections = single.data?.sections || lastSections;
        } else {
          failed.push({ item, message: single.data?.description || single.data?.message || '' });
        }
      }

      failed.forEach(({ item, message }) => {
        if (/sold out|not available|unavailable|out of stock/i.test(message)) this.#markUnavailable(item);
      });

      if (added.length) {
        await this.#onAdded(added, failed, lastSections);
      } else {
        this.#setBusy(false);
        this.render();
        this.#setStatus(this.#failureMessage(failed, true), 'error');
        this.#emitCartError(failed);
      }
    } catch (error) {
      console.error('[FBT] add to cart failed', error);
      this.#setBusy(false);
      this.render();
      this.#setStatus("We couldn't reach the store. Check your connection and try again — nothing was added.", 'error');
      this.#emitCartError([], 'network');
    }
  }

  #failureMessage(failed, nothingAdded) {
    const names = failed.map((f) => `“${this.#nameOf(f.item)}”`).join(', ');
    const reason = failed.length === 1 && failed[0].message ? ` ${failed[0].message}` : '';
    return nothingAdded
      ? `Nothing was added. ${names} couldn't be added.${reason}`
      : `${names} couldn't be added and ${failed.length === 1 ? 'was' : 'were'} left out.${reason}`;
  }

  #emitCartError(failed, kind = 'cart') {
    this.#track('fbt_add_to_cart_error', {
      productId: this.mainProductId,
      kind,
      failed: failed.map((f) => ({ productId: f.item.productId, variantId: f.item.variantId, message: f.message })),
    });
  }

  async #onAdded(added, failed, sections) {
    let cart = null;
    try {
      cart = await (await fetch(`${this.dataset.cartUrl || '/cart'}.js`, { headers: { Accept: 'application/json' } })).json();
    } catch {
      /* the event below still refreshes sections */
    }

    const addedCount = added.reduce((sum, i) => sum + i.quantity, 0);

    this.dispatchEvent(
      new CartAddEvent(cart || {}, this.id, {
        source: 'fbt-component',
        // cart-icon.js treats non product-form sources as an absolute count.
        itemCount: cart?.item_count ?? addedCount,
        productId: this.mainProductId,
        sections: sections || undefined,
      })
    );

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && !drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') drawer.open();

    this.#track('fbt_add_to_cart_success', {
      productId: this.mainProductId,
      items: added.map((i) => ({ productId: i.productId, variantId: i.variantId, quantity: i.quantity, main: Boolean(i.isMain) })),
      failed: failed.map((f) => ({ productId: f.item.productId, variantId: f.item.variantId })),
    });

    // Picks that are now in the cart are unticked (normal appearance, not "deselected"), so a
    // second click can't add them again by accident. The shopper can re-tick one on purpose.
    if (added.length) this.userTouched = true;
    added.forEach((item) => {
      if (item.isMain) return;
      item.selected = false;
      item.userDeselected = false;
    });

    this.#setBusy(false);
    this.render();

    if (failed.length) {
      this.#setStatus(`Added ${added.length} of ${added.length + failed.length} items. ${this.#failureMessage(failed, false)}`, 'error');
      this.#emitCartError(failed);
      return;
    }

    const noun = addedCount === 1 ? 'item' : 'items';
    if (drawer) {
      this.#setStatus(`${addedCount} ${noun} added to your cart.`, 'success');
    } else {
      const wrap = document.createElement('span');
      wrap.append(`${addedCount} ${noun} added to your cart. `);
      const link = document.createElement('a');
      link.href = this.dataset.cartUrl || '/cart';
      link.textContent = 'View cart';
      wrap.append(link);
      this.#setStatus(wrap, 'success');
    }

    this.addLabel.textContent = 'Added ✓';
    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => this.render(), 2200);
  }

  /* ----------------------------------------------------------- analytics */

  #track(name, payload) {
    const data = { ...payload, sectionId: this.dataset.sectionId };
    try {
      window.Shopify?.analytics?.publish?.(name, data); // Visible to Customer Events / custom pixels.
    } catch {
      /* ignore */
    }
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, fbt: data });
    this.dispatchEvent(new CustomEvent(`fbt:${name.replace(/^fbt_/, '')}`, { bubbles: true, detail: data }));
  }
}

if (!customElements.get('fbt-component')) customElements.define('fbt-component', FbtComponent);
